<template>
  <div>
    <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">
      Select Competitors to Analyze
    </h3>

    <form class="mb-4 p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50" @submit.prevent="addManualCompetitor">
      <label for="manual-competitor-url" class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
        Manual competitor URL
      </label>
      <div class="flex flex-col sm:flex-row gap-2">
        <input
          id="manual-competitor-url"
          v-model="manualCompetitorUrl"
          type="text"
          placeholder="https://competitor.com/page"
          class="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
        <button type="submit" class="btn btn-secondary btn-sm">
          Add Competitor
        </button>
      </div>
      <p v-if="manualError" class="mt-2 text-xs text-red-600 dark:text-red-400">
        {{ manualError }}
      </p>
    </form>

    <div v-if="manualSelectedUrls.length" class="mb-4 space-y-2">
      <div
        v-for="url in manualSelectedUrls"
        :key="url"
        class="flex items-center justify-between gap-3 p-3 rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20">
        <div class="min-w-0">
          <div class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
            {{ extractDomain(url) }}
          </div>
          <div class="text-xs text-gray-500 dark:text-gray-400 truncate">
            {{ url }}
          </div>
        </div>
        <button type="button" class="text-xs text-red-500 hover:text-red-600" @click="removeUrl(url)">
          Remove
        </button>
      </div>
    </div>

    <div v-if="organicResults.length === 0" class="text-center py-6">
      <p class="text-gray-500 dark:text-gray-400">
        No organic results available. Add competitor URLs manually.
      </p>
    </div>

    <div v-else class="space-y-2 mb-6">
      <label
        v-for="result in organicResults"
        :key="result.link"
        class="flex items-start gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer">
        <input
          type="checkbox"
          :value="result.link"
          v-model="selectedUrls"
          class="mt-1 rounded border-gray-300">
        <div class="flex-1 min-w-0">
          <div class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{{ result.title }}</div>
          <div class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ result.link }}</div>
          <div class="text-xs text-gray-400 mt-0.5">#{{ result.position }} &middot; {{ extractDomain(result.link) }}</div>
        </div>
      </label>
    </div>

    <div class="flex items-center justify-between">
      <span class="text-sm text-gray-500">{{ selectedUrls.length }} competitors selected</span>
      <button
        @click="$emit('start', selectedUrls)"
        :disabled="selectedUrls.length === 0"
        class="inline-flex items-center px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50">
        Start Analysis
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  serpAnalysis: { type: Object, required: true }
})

defineEmits(['start'])

const EXCLUDED_DOMAINS = ['wikipedia.org', 'youtube.com', 'yelp.com', 'facebook.com', 'twitter.com', 'instagram.com', 'pinterest.com', 'reddit.com', 'linkedin.com', 'bbb.org', 'yellowpages.com', 'amazon.com']

const extractDomain = (url) => {
  try { return new URL(url).hostname.replace(/^www\./, '') } catch { return url }
}

const organicResults = computed(() => {
  const results = props.serpAnalysis?.raw_serp_data?.organic_results || []
  return results.filter((r) => {
    const domain = extractDomain(r.link || '')
    return !EXCLUDED_DOMAINS.some((d) => domain.includes(d))
  })
})

const selectedUrls = ref(organicResults.value.slice(0, 7).map((r) => r.link))
const manualCompetitorUrl = ref('')
const manualError = ref('')
const organicUrls = computed(() => new Set(organicResults.value.map((r) => r.link)))
const manualSelectedUrls = computed(() => selectedUrls.value.filter((url) => !organicUrls.value.has(url)))

const normalizeUrl = (url) => {
  let raw = url.trim()
  if (!raw) return null
  if (!/^https?:\/\//i.test(raw)) raw = `https://${raw}`

  try {
    const parsed = new URL(raw)
    parsed.hash = ''
    if (!parsed.pathname) parsed.pathname = '/'
    return parsed.toString()
  } catch {
    return null
  }
}

const addManualCompetitor = () => {
  manualError.value = ''
  const normalized = normalizeUrl(manualCompetitorUrl.value)
  if (!normalized) {
    manualError.value = 'Enter a valid competitor URL.'
    return
  }
  if (!selectedUrls.value.includes(normalized)) {
    selectedUrls.value.push(normalized)
  }
  manualCompetitorUrl.value = ''
}

const removeUrl = (url) => {
  selectedUrls.value = selectedUrls.value.filter((selectedUrl) => selectedUrl !== url)
}
</script>
